import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
} from "react-router";
import type { LinksFunction, LoaderFunctionArgs } from "react-router";
import * as Sentry from "@sentry/react";
import "./tailwind.css";
import { getAuthFromRequest } from "./services/auth.server";
import NavBar from "./components/shared/navbar";
import Footer from "./components/shared/Footer";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Loading from "./components/Loading";
import { Route } from "./+types/root";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getAuthFromRequest(request);
  const isAuthenticated = !!session;

  return { isAuthenticated, session };
}

export function Layout({ children }: { children: React.ReactNode }) {
  const loaderData = useLoaderData<typeof loader>();
  const { isAuthenticated = false, session = null } = loaderData || {};
  const navigation = useNavigation();
  const isLoading = navigation.state === "loading";

  return (
    <html lang="pt" className="h-full bg-white">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="manifest" href="/site.webmanifest"></link>
        <link rel="preload" href="/loading.gif" as="image" />

        <Meta />
        <Links />

        <script defer data-domain="teatro.app" data-api="/plausible/api/event" src="/plausible/script.js" />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.plausible = window.plausible || function() { 
                (window.plausible.q = window.plausible.q || []).push(arguments) 
              }
            `,
          }}
        />
      </head>
      <body className="h-full bg-white">
        <div className="min-h-screen flex flex-col">
          <NavBar isAuthenticated={isAuthenticated} firstName={session?.firstName || ""} />
          <main className="flex-1 pt-14 overflow-hidden flex flex-col">{children}</main>
          <CookieConsentBanner />
          <Footer />
        </div>
        {!!isLoading && <Loading />}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Handle 404 Route Error specifically (no logging)
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <Error404 />;
  }

  // Log all other errors to Sentry, but ONLY in production
  if (!import.meta.env.DEV) {
    Sentry.captureException(error);
  } else {
    console.error("ErrorBoundary caught:", error);
  }

  // If it's a Route Error Response (but not 404, handled above)
  if (isRouteErrorResponse(error) && error.status === 500) {
    return <Error500 />;
  }

  // Fallback for anything else thrown (string, null, object, etc.)
  return <ErrorUnknown />;
}

export function ErrorContainer({ children }: { children: React.ReactNode }) {
  return (
    <main className="pt-16 pb-24 px-4 container mx-auto flex-1 flex flex-col items-center justify-center text-center">
      {children}
    </main>
  );
}

export function Error404() {
  return (
    <ErrorContainer>
      <img src="/error-404.jpg" alt="404" className="max-w-[400px] max-h-[400px] w-full h-full mb-16" />
      <h1 className="text-2xl font-bold mb-4 text-red-700">404 — Página não encontrada</h1>
      <p className="text-gray-700">Lamentamos mas parece que esta página saíu de cena.</p>
    </ErrorContainer>
  );
}

export function Error500() {
  return (
    <ErrorContainer>
      <img src="/error-500.jpg" alt="500" className="max-w-[400px] max-h-[400px] w-full h-full mb-16" />
      <h1 className="text-2xl font-bold mb-4 text-red-700">500 — Erro ao carregar a página</h1>
      <p className="text-gray-700">
        Lamentamos mas esta página teve um imprevisto.
        <br />
        Volta a tentar em breve ou regressa ao início.
      </p>
    </ErrorContainer>
  );
}

export function ErrorUnknown() {
  return (
    <ErrorContainer>
      <img src="/error-unknown.jpg" alt="unknown" className="max-w-[400px] max-h-[400px] w-full h-full mb-16" />
      <h1 className="text-2xl font-bold mb-4 text-red-700">Ocorreu um erro inesperado</h1>
      <p className="text-gray-700">
        Algo correu mal... mas não sabemos o quê.
        <br />
        Volta ao início ou tenta novamente mais tarde.
      </p>
    </ErrorContainer>
  );
}
